import dayjs from "dayjs";
import { inject, injectable } from "inversify";
import { FrontLogger } from "@whatslly/commons/frontLogger/frontLogger";
import { fromPromise } from "mobx-utils";
import { makeObservable, computed } from "mobx";
import type { IUserStore } from "@whatslly/ui-components/services/UserStore";
import { InjectUserStore } from "@whatslly/ui-components/services/UserStore";
import type { datepart } from "@whatslly/types/analytics/AnalyticsQueryFilter";
import type { SFUsersList } from "@whatslly/types/analytics/AnalyticsSFTypes";
import { getJSON } from "@whatslly/commons/apiUtils";
import { parseOrReturnError } from "@whatslly/commons/parseOrReturnError";
import { UrlStore } from "./urlStore";
import take from "lodash/take";
import { OrgConfigsStore } from "./OrgConfigsStore";

export const dateList = [
  { label: "filters.last_month", value: dayjs().subtract(1, "month").startOf("day").valueOf() },
  { label: "filters.last_60_days", value: dayjs().subtract(60, "days").startOf("day").valueOf() },
  { label: "filters.last_90_days", value: dayjs().subtract(90, "days").startOf("day").valueOf() },
];

@injectable()
export class FiltersStore {
  constructor(
    @inject(InjectUserStore) protected readonly userStore: IUserStore,
    @inject(UrlStore) protected readonly urlStore: UrlStore,
    @inject(OrgConfigsStore) protected readonly orgConfigsStore: OrgConfigsStore,
    @inject(FrontLogger) protected readonly logger: FrontLogger
  ) {
    makeObservable(this, {
      orgId: computed,
      accountId: computed,
      filteredUsers: computed,
      filteredUsersForFilter: computed,
      analyticsFilter: computed,
      startDate: computed,
      period: computed,
      usersList: computed({ keepAlive: true }),
      ticksInPeriod: computed,
      filter: computed,
    });

    const initParamValue = new URL(location.href).searchParams?.get("orgId");

    if (typeof initParamValue === "string") {
      this.orgId = initParamValue;
    }
  }

  get filter() {
    return Object.fromEntries(this.urlStore.searchParams?.entries() || []);
  }

  get usersList() {
    const urlSearchParams = new URLSearchParams();
    urlSearchParams.set("orgId", this.orgId || this.userStore.user?.organizationId || "");
    if (this.userStore.user?.profileId && this.userStore.user?.filterAnalyticsByProfile && !this.orgId) {
      urlSearchParams.set("profileId", this.userStore.user?.profileId);
    }

    return fromPromise(
      getJSON({
        urlSuffix: `/analytics/query/users_list?${urlSearchParams.toString()}`,
        etoken: this.userStore.etoken ?? "",
        pod: import.meta.env.VITE_API_DEFAULT_ANALYTICS_POD,
      })
        .then((r) => parseOrReturnError<SFUsersList>(r, this.logger))
        .then((r) => new Map(Object.entries(r) as [keyof SFUsersList, SFUsersList[keyof SFUsersList]][]))
    );
  }

  get orgId() {
    return this.urlStore.searchParams?.get("orgId") ?? "";
  }

  set orgId(orgId) {
    if (orgId === this.orgId) {
      return;
    }
    this.urlStore.setSearchParams({ orgId, userId: undefined, restrictToDirectReports: undefined });
  }

  get restrictToDirectReports() {
    return this.urlStore.searchParams?.get("restrictToDirectReports") === "true";
  }

  set restrictToDirectReports(restrictToDirectReports) {
    this.urlStore.setSearchParams({ restrictToDirectReports: restrictToDirectReports ? "true" : "false" });
  }

  get accountId() {
    return this.urlStore.searchParams?.get("accountId") ?? "";
  }

  set accountId(accountId: string) {
    if (accountId === this.accountId) {
      return;
    }

    this.urlStore.setSearchParams({ accountId });
  }

  get filteredUsers(): Set<string> {
    return new Set((this.urlStore.searchParams?.get("userId") ?? "").split(",").filter(Boolean));
  }

  set filteredUsers(users) {
    this.urlStore.setSearchParams({ userId: Array.from(users).join(",") });
  }

  get startDate() {
    return this.urlStore.searchParams?.get("startDate")
      ? dayjs(this.urlStore.searchParams?.get("startDate")).startOf("day").valueOf()
      : dateList[0].value;
  }

  set startDate(date) {
    this.urlStore.setSearchParams({ startDate: dayjs(date).toISOString() });
  }

  get endDate() {
    const date = this.urlStore.searchParams?.get("endDate") ? dayjs(this.urlStore.searchParams?.get("endDate")) : dayjs();
    return date.endOf("day").valueOf();
  }

  set endDate(date) {
    this.urlStore.setSearchParams({ endDate: dayjs(date).toISOString() });
  }

  get period() {
    const start = dayjs(this.startDate);
    const end = dayjs(this.endDate);
    const days = end.diff(start, "day");
    const months = end.diff(start, "month");

    // If less than 2 months, return days
    if (Math.abs(months) < 2) {
      return { count: days, type: "day" as const };
    }

    // Otherwise return months
    return { count: months, type: "month" as const };
  }

  public setSearchParams(params: Record<string, string | string[] | undefined>) {
    Object.entries(params).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        this.urlStore.searchParams?.delete(key);
        value.forEach((val) => {
          this.urlStore.searchParams?.append(key, val);
        });
      } else if (!value?.length) {
        this.urlStore.searchParams?.delete(key);
      } else {
        this.urlStore.searchParams?.set(key, value);
      }
    });

    this.urlStore.setParams?.(this.urlStore.searchParams ?? {});
  }

  get filteredUsersForFilter() {
    if (!this.userStore.user?.filterAnalyticsByProfile || this.filteredUsers.size > 0 || this.orgId) {
      return this.filteredUsers;
    }
    return new Set(this.usersList.state === "fulfilled" ? take([...this.usersList.value.keys()], 300) : []);
  }

  get isRestrictedToDirectReportsByDefault() {
    const bypassList = this.orgConfigsStore.getConfig("restrict_to_direct_reports_bypass_list");
    return (
      this.orgConfigsStore.getConfig("restrict_to_direct_reports") === true &&
      bypassList?.[this.userStore.user?._id ?? ""] !== true &&
      !this.userStore.isInternalUser
    );
  }

  get analyticsFilter() {
    const search = new URLSearchParams({});
    search.set("startDate", this.startDate.valueOf().toString());
    search.set("endDate", this.endDate.valueOf().toString());
    search.set("orgId", this.orgId);
    search.set(
      "restrictToDirectReports",
      this.urlStore.searchParams?.get("restrictToDirectReports") ?? (this.isRestrictedToDirectReportsByDefault ? "true" : "false")
    );
    this.filteredUsersForFilter.forEach((userId) => search.append("userId", userId));
    this.filter.groupId?.split(",")?.forEach((groupId) => search.append("groupId", groupId));
    return search.toString();
  }

  get datePart(): datepart {
    // TODO: Automatically set datepart relative to date range
    // e.g. if range is less than a week, use "hour",
    // if range is 1 month, use "day", if range is 1 year, use "month"
    // if range is more than 1 year, use "quarter" etc
    return "day";
  }

  get ticksInPeriod() {
    const diff = dayjs(this.endDate).diff(this.startDate, this.datePart) + 1;
    return Array.from(new Array(diff), (_, i) => dayjs(this.endDate).subtract(diff - i - 1, this.datePart));
  }
}
